import type { ResponseData } from '@vrplatform/data';
import { day, toTitleCase } from '@vrplatform/utils';

export type JournalStatus =
  ResponseData<'get:/reservations/{id}'>['journalStatus'];
export type JournalResource = Pick<
  ResponseData<'get:/reservations/{id}'>,
  'journalStatus' | 'operations'
>;

export const getJournalFreshnessLabel = (
  state: JournalStatus['state'],
  subject: 'figures' | 'financials' | 'statement' = 'figures'
) => {
  switch (state) {
    case 'current':
      return undefined;
    case 'recalculating':
      return subject === 'statement'
        ? 'Updating statement figures…'
        : `Updating ${subject}…`;
    case 'stale':
      return subject === 'statement'
        ? 'Statement figures need updating'
        : `${toTitleCase(subject)} may be outdated`;
    case 'failed':
      return subject === 'statement'
        ? 'Statement figures could not be updated'
        : `${toTitleCase(subject)} could not be updated`;
  }
};

const STATEMENT_BLOCKED_MESSAGE =
  'This statement cannot be finalized and a payment cannot be created until the update issue is resolved.';

// Statement wording names the blocked finalization and payment actions; other
// subjects keep their generic freshness copy.
const getStatementJournalDetails = ({
  journalStatus,
  failureMessage,
  listsFailures,
}: {
  journalStatus: JournalStatus;
  failureMessage?: string;
  listsFailures: boolean;
}) => {
  const pending = journalStatus.state === 'recalculating';
  const locked =
    journalStatus.state === 'failed' &&
    'reason' in journalStatus &&
    journalStatus.reason === 'journalLocked';
  const hasListedFailures =
    listsFailures &&
    journalStatus.state === 'failed' &&
    'failures' in journalStatus &&
    !!journalStatus.failures?.length;
  // The API failure message names its own next step, so the generic support
  // sentence only follows when no failure message is available.
  const message = pending
    ? 'Recent changes are being applied. You can keep reviewing this statement, but its amounts may change. Wait for the update to finish before finalizing the statement or creating a payment.'
    : journalStatus.state === 'stale'
      ? 'Recent changes are not yet reflected in this statement. The statement must finish updating before you can finalize it or create a payment.'
      : locked
        ? 'Some reservation changes affect closed books or a locked statement and could not be applied. Review the affected reservations to determine the correction needed. This statement cannot be finalized and a payment cannot be created until the issue is resolved.'
        : hasListedFailures
          ? `Some reservation changes could not be applied, so the statement amounts may be out of date. Review the reservation issues below. ${STATEMENT_BLOCKED_MESSAGE}`
          : failureMessage
            ? `${failureMessage} The statement amounts may be out of date. ${STATEMENT_BLOCKED_MESSAGE}`
            : `The statement amounts may be out of date. ${STATEMENT_BLOCKED_MESSAGE} Contact support if you need help resolving the update issue.`;

  return [
    message,
    journalStatus.staleSince
      ? `Updates pending since ${day(journalStatus.staleSince).format('MMM D, YYYY h:mm A')}.`
      : '',
    journalStatus.calculatedAt
      ? `Last calculation: ${day(journalStatus.calculatedAt).format('MMM D, YYYY h:mm A')}.`
      : '',
    !pending &&
    journalStatus.staleSince &&
    journalStatus.calculatedAt &&
    day(journalStatus.calculatedAt).isAfter(journalStatus.staleSince)
      ? 'Some changes are still pending, even after the last calculation.'
      : '',
  ]
    .filter(Boolean)
    .join(' ');
};

export const getJournalFreshnessCopy = ({
  journalStatus,
  failureMessage,
  subject = 'financials',
  hasAction = false,
  listsFailures = false,
}: {
  journalStatus: JournalStatus;
  failureMessage?: string;
  subject?: 'figures' | 'financials' | 'statement';
  hasAction?: boolean;
  // The caller renders the statement's failing reservations with this copy.
  listsFailures?: boolean;
}) => {
  const pending = journalStatus.state === 'recalculating';
  const locked =
    journalStatus.state === 'failed' &&
    'reason' in journalStatus &&
    journalStatus.reason === 'journalLocked';
  const failed = journalStatus.state === 'failed' && !locked;
  // On a statement an accounting lock is a blocking state: the guard rejects
  // finalization and payment for every non-current status, locks included.
  const blockingLock = locked && subject === 'statement';
  const label = locked
    ? subject === 'statement'
      ? 'Accounting locks blocked statement updates'
      : 'Accounting review needed'
    : getJournalFreshnessLabel(journalStatus.state, subject);
  if (subject === 'statement') {
    return {
      label,
      details: getStatementJournalDetails({
        journalStatus,
        failureMessage,
        listsFailures,
      }),
      pending,
      failed,
      blockingLock,
    };
  }
  const message = pending
    ? `You can keep reading while these ${subject} update automatically.`
    : `${locked ? 'Accounting locks prevented pending journal changes from being applied. ' : failed && failureMessage ? `${failureMessage} ` : ''}The displayed ${subject} may be outdated.${locked ? ' Review the affected reservations to determine whether a correction is needed.' : hasAction ? ' Review the issue before trying again.' : failed && failureMessage ? '' : ' Please contact support if you cannot resolve the issue.'}`;
  const details = [
    message,
    journalStatus.staleSince
      ? `${pending ? 'Pending' : 'Unresolved'} since ${day(journalStatus.staleSince).format('MMM D, YYYY h:mm A')}.`
      : '',
    journalStatus.calculatedAt
      ? `Last calculated ${day(journalStatus.calculatedAt).format('MMM D, YYYY h:mm A')}.`
      : '',
    !pending && journalStatus.staleSince && journalStatus.calculatedAt
      ? 'A later calculation does not mean all pending changes were applied.'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return { label, details, pending, failed, blockingLock };
};

export const getJournalFailureMessage = (
  operations: JournalResource['operations']
) =>
  operations.items
    .filter(
      (operation) =>
        operation.type === 'reservation-journal-refresh' &&
        operation.status === 'failed'
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.failure
    ?.message;

export const isJournalRecalculationPending = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  error.code === 'JOURNAL_RECALCULATION_PENDING';

export const JOURNAL_ACTION_BLOCKED_MESSAGE =
  'Statement figures must finish updating before you can finalize the statement or create a payment.';
